import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    CircularProgress,
    Drawer,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';

import {
    setTheme,
    useAppDispatch,
    useLastNewsQuery,
    useNewsAllSeenMutation,
    useNewsSeenMutation,
    useUserProfileQuery,
} from '../store';
import SidebarComponents from './sidebar';
import { userAreaMenu } from './sidebar.constants';
import useBreakpoint from '../../hooks/use-breakpoint';
import { UserStatus } from 'features/profile/types/user.types';
import { immediateToast } from 'utils/toast';
import { NotificationItem } from 'features/news/components/notification-item';
import { NewsItemType } from 'features/news/news.types';
import ShowNewsDialog from 'features/news/pages/show-news';
import { EngToPerConverter } from 'utils/utils';

function Notifications() {
    const [newsAllSeen, { isLoading: isSettingAllSeen }] =
        useNewsAllSeenMutation();
    const [newsSeen, { isLoading: isSettingSeen }] = useNewsSeenMutation();
    const { data: newsList, isFetching } = useLastNewsQuery();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedNews, setSelectedNews] = React.useState<NewsItemType | null>(null);
    const [readNewsIds, setReadNewsIds] = React.useState<Set<number>>(() => new Set());
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const open = Boolean(anchorEl);
    const items: NewsItemType[] = newsList?.items ?? [];
    const unreadCount = Number(newsList?.unread_news ?? 0);
    const hasItems = items.length > 0;
    const canReadAll = items.length > 1 && unreadCount > 0;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOpenNews = (notification: NewsItemType) => {
        setSelectedNews({
            ...notification,
            read_news:
                notification.read_news === true || readNewsIds.has(notification.id),
        });
        handleClose();
        setIsDialogOpen(true);
    };

    const handleCloseNews = () => {
        if (isSettingSeen) return;
        setIsDialogOpen(false);
    };

    const handleReadSelected = () => {
        if (!selectedNews) return;

        if (selectedNews.read_news) {
            setIsDialogOpen(false);
            return;
        }

        newsSeen(selectedNews.id)
            .unwrap()
            .then(() => {
                setReadNewsIds((current) => {
                    const next = new Set(current);
                    next.add(selectedNews.id);
                    return next;
                });
                setSelectedNews((current) =>
                    current ? { ...current, read_news: true } : current,
                );
                setIsDialogOpen(false);
            })
            .catch(() => {
                immediateToast('error', {
                    message: 'ثبت وضعیت اطلاعیه انجام نشد. دوباره تلاش کنید.',
                    timeout: 4000,
                });
            });
    };

    const handleNewsAllSeen = () => {
        newsAllSeen()
            .unwrap()
            .then(() => {
                immediateToast('success', {
                    message: 'همه اطلاعیه‌ها خوانده شدند.',
                    timeout: 1800,
                });
            })
            .catch(() => {
                immediateToast('error', {
                    message: 'تغییر وضعیت اطلاعیه‌ها انجام نشد. دوباره تلاش کنید.',
                    timeout: 4000,
                });
            });
    };

    const badgeContent =
        unreadCount > 99
            ? '۹۹+'
            : unreadCount > 0
              ? EngToPerConverter(String(unreadCount))
              : undefined;

    return (
        <>
            <IconButton
                onClick={handleClick}
                aria-label="اطلاعیه‌ها"
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'border.light',
                    backgroundColor: alpha(theme.palette.background.paper, 0.72),
                    '&:hover': {
                        color:
                            theme.palette.mode === 'dark'
                                ? 'primary.light'
                                : 'primary.main',
                        borderColor: alpha(
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            0.28,
                        ),
                        backgroundColor: alpha(
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            0.065,
                        ),
                    },
                })}>
                <Badge
                    badgeContent={badgeContent}
                    invisible={unreadCount <= 0}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={(theme) => ({
                        '@keyframes notificationBellRing': {
                            '0%, 76%, 100%': { transform: 'rotate(0deg)' },
                            '80%': { transform: 'rotate(-13deg)' },
                            '84%': { transform: 'rotate(11deg)' },
                            '88%': { transform: 'rotate(-8deg)' },
                            '92%': { transform: 'rotate(6deg)' },
                            '96%': { transform: 'rotate(-2.5deg)' },
                        },
                        '& > .notification-bell-icon': {
                            display: 'inline-block',
                            transformOrigin: '50% 8%',
                            willChange: 'transform',
                            animation:
                                unreadCount > 0
                                    ? 'notificationBellRing 3.15s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite'
                                    : 'none',
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                            },
                        },
                        '& .MuiBadge-badge': {
                            minWidth: 17,
                            height: 17,
                            px: 0.45,
                            top: -2,
                            left: -4,
                            borderRadius: '10px',
                            border: `2px solid ${theme.palette.background.paper}`,
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.common.white,
                            fontSize: 9,
                            fontFamily: 'YekanBalkhHeavy',
                            lineHeight: 1,
                        },
                    })}>
                    <i
                        className="fa-light fa-bell notification-bell-icon"
                        style={{ fontSize: '20px' }}
                    />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                MenuListProps={{
                    sx: {
                        p: 0,
                        overflow: 'hidden',
                    },
                }}
                PaperProps={{
                    sx: (theme) => ({
                        mt: 1,
                        width: 'min(390px, calc(100vw - 16px))',
                        maxWidth: 'calc(100vw - 16px)',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.09)
                                : alpha(theme.palette.common.black, 0.09),
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.985)
                                : theme.palette.common.white,
                        backgroundImage: 'none',
                        boxShadow:
                            theme.palette.mode === 'dark'
                                ? `0 20px 60px ${alpha(theme.palette.common.black, 0.48)}`
                                : `0 18px 48px ${alpha('#172033', 0.14)}`,
                    }),
                }}>
                <Box
                    sx={(theme) => ({
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        minHeight: 54,
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        borderBottom: '1px solid',
                        borderColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.08)
                                : alpha(theme.palette.common.black, 0.075),
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.98)
                                : alpha(theme.palette.common.white, 0.98),
                        backdropFilter: 'blur(12px)',
                    })}>
                    <Typography
                        className="font-yekan-heavy"
                        sx={{ fontSize: 14, color: 'text.primary' }}>
                        آخرین اطلاعیه‌ها
                    </Typography>

                    {canReadAll && (
                        <Button
                            onClick={handleNewsAllSeen}
                            disabled={isSettingAllSeen}
                            disableRipple
                            startIcon={
                                isSettingAllSeen ? (
                                    <CircularProgress size={13} color="inherit" />
                                ) : (
                                    <i
                                        className="fa-solid fa-check-double"
                                        style={{ fontSize: 10 }}
                                    />
                                )
                            }
                            sx={(theme) => ({
                                minWidth: 0,
                                minHeight: 30,
                                px: 0.8,
                                py: 0.2,
                                borderRadius: 1.5,
                                fontSize: 11,
                                color:
                                    theme.palette.mode === 'dark'
                                        ? 'primary.light'
                                        : 'primary.main',
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        0.08,
                                    ),
                                },
                            })}>
                            خواندن همه
                        </Button>
                    )}
                </Box>

                {isFetching && !newsList ? (
                    <Box
                        sx={{
                            minHeight: 170,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <CircularProgress size={25} />
                    </Box>
                ) : hasItems ? (
                    <Box
                        sx={(theme) => ({
                            maxHeight: 324,
                            overflowY: items.length > 3 ? 'auto' : 'hidden',
                            overflowX: 'hidden',
                            overscrollBehavior: 'contain',
                            '&::-webkit-scrollbar': {
                                width: 6,
                            },
                            '&::-webkit-scrollbar-button, &::-webkit-scrollbar-button:single-button': {
                                display: 'none',
                                width: 0,
                                height: 0,
                                minWidth: 0,
                                minHeight: 0,
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-button:vertical:start:decrement, &::-webkit-scrollbar-button:vertical:end:increment': {
                                display: 'none',
                                width: 0,
                                height: 0,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: 99,
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.16)
                                        : alpha(theme.palette.common.black, 0.16),
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.24)
                                        : alpha(theme.palette.common.black, 0.24),
                            },
                        })}
                    >
                        {items.map((notif, index) => (
                            <NotificationItem
                                key={notif?.id ?? index}
                                notif={notif}
                                index={index}
                                isLastItem={index === items.length - 1}
                                onOpen={handleOpenNews}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            minHeight: 205,
                            px: 3,
                            py: 2.75,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}>
                        <Box
                            component="img"
                            src="/images/no-exist.png"
                            alt="اطلاعیه‌ای وجود ندارد"
                            sx={(theme) => ({
                                width: 88,
                                maxWidth: '34%',
                                mb: 1.2,
                                opacity: theme.palette.mode === 'dark' ? 0.82 : 0.7,
                                filter:
                                    theme.palette.mode === 'light'
                                        ? 'grayscale(0.1) contrast(0.94)'
                                        : 'brightness(0.92)',
                            })}
                        />
                        <Typography
                            className="font-yekan-heavy"
                            sx={{ fontSize: 13, color: 'text.secondary' }}>
                            اطلاعیه‌ای برای نمایش وجود ندارد
                        </Typography>
                    </Box>
                )}
            </Menu>

            <ShowNewsDialog
                news={selectedNews}
                isOpen={isDialogOpen}
                onClose={handleCloseNews}
                onRead={handleReadSelected}
                isReading={isSettingSeen}
                showReadAction={selectedNews?.read_news !== true}
            />
        </>
    );
}

function UserAreaSection() {
    // API
    const { data: profile } = useUserProfileQuery();
    // State
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    // Router
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogOut = () => {
        handleClose();
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                svg: { color: 'text.primary' },
            }}>
            {profile?.status === UserStatus.accept && <Notifications />}
            <Avatar
                variant="rounded"
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    fontSize: 14,
                    ml: 0.75,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    outline: `1px solid ${theme.palette.border.main}`,
                    backgroundColor: profile?.avatar_color,
                    cursor: 'pointer',
                    color: 'primary.contrastText',
                    boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.1)}`,
                    transition: theme.transitions.create(['transform', 'box-shadow'], { duration: 140 }),
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.14)}`,
                    },
                })}
                src={profile?.avatar}
                alt={profile?.name}
                onClick={handleClick}
                children={profile?.name?.substring(0, 1)}
            />

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                MenuListProps={{ sx: { p: 0 } }}
                PaperProps={{
                    elevation: 0,
                    sx: (theme) => ({
                        mt: 1,
                        width: 270,
                        maxWidth: 'calc(100vw - 16px)',
                        overflow: 'hidden',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.09)
                                : alpha(theme.palette.common.black, 0.08),
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.985)
                                : theme.palette.background.paper,
                        backgroundImage: 'none',
                        boxShadow:
                            theme.palette.mode === 'dark'
                                ? `0 18px 46px ${alpha(theme.palette.common.black, 0.42)}`
                                : `0 16px 42px ${alpha('#172033', 0.13)}`,
                    }),
                }}>
                <Box
                    sx={(theme) => ({
                        px: 1.5,
                        py: 1.4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: alpha(
                            theme.palette.text.primary,
                            theme.palette.mode === 'dark' ? 0.025 : 0.016,
                        ),
                    })}
                >
                    <Avatar
                        variant="rounded"
                        src={profile?.avatar}
                        sx={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            backgroundColor: profile?.avatar_color,
                            fontSize: 13,
                        }}
                    >
                        {profile?.name?.substring(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            className="font-yekan-heavy"
                            noWrap
                            sx={{ fontSize: 13.5, color: 'text.primary' }}
                        >
                            {profile?.name} {profile?.family}
                        </Typography>
                        {profile?.account ? (
                            <Typography
                                noWrap
                                style={{ textAlign: 'right' }}
                                sx={{
                                    mt: 0.15,
                                    width: '100%',
                                    display: 'block',
                                    direction: 'ltr',
                                    color: 'text.secondary',
                                    fontFamily: 'IRANSans, Tahoma, Arial, sans-serif',
                                    fontSize: 10.5,
                                }}
                            >
                                {profile.account}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>

                <Box sx={{ p: 0.75 }}>
                    {userAreaMenu.map((item) => (
                        <MenuItem
                            key={item.url}
                            disabled={profile?.status !== 'accept'}
                            onClick={() => {
                                if (item.url) navigate(item.url);
                                handleClose();
                            }}
                            sx={(theme) => ({
                                minHeight: 42,
                                px: 1.1,
                                py: 0.65,
                                gap: 1,
                                borderRadius: '6px',
                                color: 'text.primary',
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.085 : 0.055,
                                    ),
                                },
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    width: 30,
                                    height: 30,
                                    flexShrink: 0,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: '4px',
                                    color:
                                        theme.palette.mode === 'dark'
                                            ? 'primary.light'
                                            : 'primary.main',
                                    backgroundColor: alpha(
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.09 : 0.055,
                                    ),
                                    '& i': { fontSize: '15px !important' },
                                })}
                            >
                                {item.icon}
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: 12.5, lineHeight: 1.7 }}
                            >
                                {item.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Box>

                <Box sx={{ px: 0.75, pb: 0.75 }}>
                    <MenuItem
                        onClick={handleLogOut}
                        sx={(theme) => ({
                            minHeight: 42,
                            px: 1.1,
                            py: 0.65,
                            gap: 1,
                            borderRadius: '6px',
                            color: 'error.main',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.065),
                            },
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '4px',
                                color: 'error.main',
                                backgroundColor: alpha(theme.palette.error.main, 0.07),
                            })}
                        >
                            <i
                                className="fa-light fa-power-off"
                                style={{ fontSize: '15px' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                            خروج از حساب
                        </Typography>
                    </MenuItem>
                </Box>
            </Menu>
        </Box>
    );
}

export function ThemeToggle() {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const handleToggleTheme = () => {
        const nextMode = theme.palette.mode === 'light' ? 'dark' : 'light';
        dispatch(setTheme(nextMode));
    };

    const isLight = theme.palette.mode === 'light';

    return (
        <Tooltip title={isLight ? 'حالت تاریک' : 'حالت روشن'}>
            <IconButton
                onClick={handleToggleTheme}
                aria-label={isLight ? 'فعال کردن حالت تاریک' : 'فعال کردن حالت روشن'}
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    border: '1px solid',
                    borderColor: 'border.light',
                    backgroundColor: alpha(theme.palette.background.paper, 0.72),
                    color: 'text.secondary',
                    '&:hover': {
                        color:
                            theme.palette.mode === 'dark'
                                ? 'primary.light'
                                : 'primary.main',
                        borderColor: alpha(
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            0.28,
                        ),
                        backgroundColor: alpha(
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                            0.065,
                        ),
                    },
                })}
            >
                <i
                    className={isLight ? 'fa-light fa-moon-stars' : 'fa-light fa-sun-bright'}
                    style={{ fontSize: 17 }}
                />
            </IconButton>
        </Tooltip>
    );
}

export default function NavLayout() {
    moment.loadPersian({ dialect: 'persian-modern' });
    const today = EngToPerConverter(
        moment().locale('fa').format('dddd jD jMMMM jYYYY'),
    );

    const [visibleDrawer, updateVisibleDrawer] = useState<boolean>(false);
    const { lowerThan } = useBreakpoint();
    const { data: profile } = useUserProfileQuery();

    const location = useLocation();

    useEffect(() => {
        updateVisibleDrawer(false);
    }, [location]);

    const toggleDrawer =
        (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
            localStorage.setItem('sidebar_collapse', !!open ? 'true' : 'false');
            updateVisibleDrawer(open);
        };

    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                minHeight: 66,
                px: { xs: 1.25, sm: 1.5, md: 1.75 },
                py: 1.05,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                direction: 'ltr',
                flexShrink: 0,
                backgroundColor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'dark' ? 0.88 : 0.82,
                ),
                backdropFilter: 'blur(18px) saturate(120%)',
                WebkitBackdropFilter: 'blur(18px) saturate(120%)',
                borderRadius: { xs: 0, md: '18px' },
                border: '1px solid',
                borderColor: 'border.light',
                boxShadow:
                    theme.palette.mode === 'light'
                        ? `0 12px 34px ${alpha('#172033', 0.055)}`
                        : `0 12px 34px ${alpha('#000000', 0.24)}`,
                position: 'relative',
                zIndex: 10,
            })}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.primary',
                }}>
                {lowerThan('sm') && profile?.status === 'accept' && (
                    <IconButton
                        onClick={toggleDrawer(true)}
                        sx={(theme) => ({
                            width: 40,
                            height: 40,
                            marginInlineEnd: { xs: 0.5, sm: 1 },
                            fontSize: '20px',
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: 'border.light',
                            backgroundColor: alpha(theme.palette.background.paper, 0.7),
                        })}>
                        <i className="fa-light fa-bars"></i>
                    </IconButton>
                )}

                {!lowerThan('sm') && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8,
                            px: 1.25,
                            py: 0.7,
                            borderRadius: '10px',
                            backgroundColor: 'background.extraLight',
                            border: '1px solid',
                            borderColor: 'border.light',
                            direction: 'rtl',
                            color: 'text.secondary',
                        }}
                    >
                        <i
                            className="fa-light fa-calendar"
                            style={{ fontSize: '14px' }}
                        />
                        <Typography
                            sx={{
                                mt: '1px',
                                fontSize: 12,
                                color: 'text.secondary',
                                fontFamily: 'YekanBalkhHeavy',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {today}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'text.primary',
                    direction: 'rtl',
                }}>
                <UserAreaSection />
                <ThemeToggle />
            </Box>

            <Drawer
                anchor="left"
                open={visibleDrawer}
                onClose={toggleDrawer(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '0px 18px 18px 0px',
                        overflow: 'hidden',
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        '.sidebar-wrapper': {
                            display: 'block',
                        },
                    }}
                    role="presentation">
                    <SidebarComponents
                        collapse={true}
                        disableCollapse={true}
                    />
                </Box>
            </Drawer>
        </Box>
    );
}
